import { getRandomColor } from "@gtmhub/whiteboards/rendering/util";
import { ICollection } from "@gtmhub/models";
import { NavItemsType, INavItem } from "../models/nav-items-list.models";
import { UXCustomizationType } from "@gtmhub/uxcustomization/models";
import { ITeam } from "@gtmhub/teams";
import { ITask } from "@gtmhub/tasks";
import { IList } from "@gtmhub/lists";
import { IInsightboard } from "@gtmhub/insightboards";
import { IViewHistoryItemsGroupedResponse, ViewHistoryItemGrouped } from "@gtmhub/view-history";
import { IPlanningSession } from "@gtmhub/sessions";
import { IKpiGroup } from "@gtmhub/kpis";
import { IEmployee } from "@gtmhub/employees";
import { FavoritesItemGrouped, IFavoritesItemsGroupedResponse } from "@gtmhub/favorites/models";

interface IMergableCollection {
  collection: IEntityNavItem[];
  transformer(item: IEntityNavItem): INavItem;
  type: UXCustomizationType;
}

interface IMergeItem {
  navItem: INavItem;
  type: UXCustomizationType;
  sortProp: string;
}

interface IEntityNavItem {
  id?: string;
  title?: string;
  name?: string;
  names?: string;
  color?: string;
  icon?: string;
  lastVisited?: string;
  picture?: string;
  avatar?: string;
  uxcItem?: ViewHistoryItemGrouped | FavoritesItemGrouped;
}

type NavItemDecorator = (navItem: INavItem, entity: ViewHistoryItemGrouped | FavoritesItemGrouped) => INavItem;
interface IResponseTranfosmersOptions {
  orderPropName: string;
  orderAll: boolean;
  decorator: NavItemDecorator;
}

export const MAX_COUNT = 15;

export const toNavItemsCollectionTransformer = <T extends IEntityNavItem>(collection: ICollection<T>, uxcType: UXCustomizationType) => {
  const navItemsCollection: ICollection<INavItem> = {
    items: collection.items.map((item) => entityNavItemToNavItem(item, uxcType, item.uxcItem)),
    totalCount: collection.items.length,
  };

  return navItemsCollection;
};

const mergeAndSortViewHistoryNavItems = (collections: IMergableCollection[], sortPropName?: string): INavItem[] => {
  const mergedItems: IMergeItem[] = collections.reduce((previous: IMergeItem[], current: IMergableCollection) => {
    return [
      ...previous,
      ...current.collection.map((item: IEntityNavItem) => {
        return {
          navItem: current.transformer(item),
          type: current.type,
          sortProp: item[sortPropName],
        };
      }),
    ];
  }, []);
  if (sortPropName) {
    mergedItems.sort((lhs, rhs) => {
      return lhs.sortProp === rhs.sortProp ? 0 : lhs.sortProp > rhs.sortProp ? -1 : 1;
    });
  }
  return mergedItems.map((item) => item.navItem);
};

const groupedReponseToNavItems = (
  collection: IViewHistoryItemsGroupedResponse | IFavoritesItemsGroupedResponse,
  type: NavItemsType,
  options: IResponseTranfosmersOptions
): ICollection<INavItem> => {
  const navItemsCollection: ICollection<INavItem> = {
    items: [],
    totalCount: collection.totalCount,
  };

  switch (type) {
    case "sessions":
      navItemsCollection.items = collection.sessionItems
        ? collection.sessionItems.map((sessionItem) => entityNavItemToNavItem(sessionItem.session, "session", sessionItem, options.decorator))
        : [];
      break;
    case "lists":
      navItemsCollection.items = collection.listItems
        ? collection.listItems.map((listItem) => entityNavItemToNavItem(listItem.list, "list", listItem, options.decorator))
        : [];
      break;
    case "insightboards":
      navItemsCollection.items = collection.dashboardItems
        ? collection.dashboardItems.map((insightItem) => entityNavItemToNavItem(insightItem.dashboard, "dashboard", insightItem, options.decorator))
        : [];
      break;
    case "whiteboards":
      navItemsCollection.items = collection.whiteboardItems
        ? collection.whiteboardItems.map((whiteboardItem) => entityNavItemToNavItem(whiteboardItem.whiteboard, "whiteboard", whiteboardItem, options.decorator))
        : [];
      break;
    case "tasks":
      navItemsCollection.items = collection.taskItems
        ? collection.taskItems.map((taskItem) => entityNavItemToNavItem(taskItem.task, "task", taskItem, options.decorator))
        : [];
      break;
    case "kpis":
      navItemsCollection.items = collection.kpiGroupsItems
        ? collection.kpiGroupsItems.map((kpiGroupItem) => entityNavItemToNavItem(kpiGroupItem.kpiGroup, "kpigroup", kpiGroupItem.kpiGroup, options.decorator))
        : [];
      break;
    case "people":
      {
        const collectionsToMerge: IMergableCollection[] = [
          {
            collection: (collection.userItems || []).map((item) => {
              return { ...item.user, lastVisited: item.lastVisited, uxcItem: item };
            }),
            transformer: (item) => entityNavItemToNavItem(item, "user", item.uxcItem, options.decorator),
            type: "user",
          },
          {
            collection: (collection.teamItems || []).map((item) => {
              return { ...item.team, lastVisited: item.lastVisited, uxcItem: item };
            }),
            transformer: (item) => entityNavItemToNavItem(item, "team", item.uxcItem, options.decorator),
            type: "team",
          },
        ];
        navItemsCollection.items = mergeAndSortViewHistoryNavItems(collectionsToMerge, options.orderPropName);
      }
      break;
  }

  return navItemsCollection;
};

export const viewHistoryGroupedReponseToNavItems = (collection: IViewHistoryItemsGroupedResponse, type: NavItemsType): ICollection<INavItem> => {
  const options = {
    orderPropName: "lastVisited",
    orderAll: false,
    decorator: decorateNavItemWithViewHistory,
  };
  return groupedReponseToNavItems(collection, type, options);
};

export const favoritesGroupedReponseToNavItems = (collection: IFavoritesItemsGroupedResponse, type: NavItemsType): ICollection<INavItem> => {
  const options = {
    orderPropName: "order",
    orderAll: true,
    decorator: decorateNavItemWithFavorite,
  };
  return groupedReponseToNavItems(collection, type, options);
};

const entityNavItemToNavItem = (
  navItemEntity: IEntityNavItem,
  type: UXCustomizationType,
  uxcEntity?: ViewHistoryItemGrouped | FavoritesItemGrouped,
  decorator?: NavItemDecorator
): INavItem => {
  const baseNavItem: INavItem = {
    id: navItemEntity.id,
    uxcType: type,
    color: navItemEntity.color || getRandomColor(),
    title: navItemEntity.title || navItemEntity.name || navItemEntity.names,
    icon: navItemEntity.icon || navItemEntity.picture || navItemEntity.avatar,
  };
  return decorator ? decorator(baseNavItem, uxcEntity) : baseNavItem;
};

const decorateNavItemWithViewHistory = (navItem: INavItem, viewHistoryItem: ViewHistoryItemGrouped): INavItem => {
  return {
    ...navItem,
    lastVisited: viewHistoryItem.lastVisited,
  };
};

const decorateNavItemWithFavorite = (navItem: INavItem, favoriteItem: FavoritesItemGrouped): INavItem => {
  return {
    ...navItem,
    order: favoriteItem.order,
    favoriteId: favoriteItem.id,
  };
};

const basicBuild = (id: string, title: string, type: UXCustomizationType) => {
  return {
    id: id,
    title: title,
    uxcType: type,
  };
};

export const favoriteUXCResponseToNavItem = (uxcReponseEntity: IEntityNavItem, type: UXCustomizationType, favoriteItem: FavoritesItemGrouped): INavItem => {
  return decorateNavItemWithFavorite(entityNavItemToNavItem(uxcReponseEntity, type), favoriteItem);
};

export const navItemsFactory = {
  buildWhiteboardNavItem: (whiteboard: { id: string; name: string }): INavItem => {
    return basicBuild(whiteboard.id, whiteboard.name, "whiteboard");
  },
  buildTaskNavItem: (task: ITask): INavItem => {
    return basicBuild(task.id, task.name, "task");
  },
  buildInsightboardNavItem: (insightboard: IInsightboard): INavItem => {
    return basicBuild(insightboard.id, insightboard.name, "dashboard");
  },
  buildKpiGroupNavItem: (kpiGroup: IKpiGroup): INavItem => {
    return basicBuild(kpiGroup.id, kpiGroup.name, "kpigroup");
  },
  buildTeamNavItem: (team: ITeam): INavItem => {
    return {
      ...basicBuild(team.id, team.name, "team"),
      color: team.color,
    };
  },
  buildEmployeeNavItem: (employee: IEmployee): INavItem => {
    return basicBuild(employee.id, `${employee.firstName} ${employee.lastName}`, "user");
  },
  buildListNavItem: (list: Partial<IList>): INavItem => {
    return {
      ...basicBuild(list.id, list.title, "list"),
      color: list.color,
    };
  },
  buildSessionNavItem: (session: IPlanningSession): INavItem => {
    return {
      ...basicBuild(session.id, session.title, "session"),
      color: session.color,
    };
  },
  withRandomColor: (navItem: INavItem): INavItem => {
    return {
      ...navItem,
      color: getRandomColor(),
    };
  },
};
